// implement a max value max that can be added or a stepper to the quantity component

import {
  onCleanup,
  createRoot,
  createSignal,
  createEffect,
  on,
  mergeProps,
  splitProps,
} from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/+esm";
import { insert } from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/web/+esm";
import {
  register,
  createContext,
  provide,
  consume,
  compose,
  noShadowDOM,
} from "https://cdn.jsdelivr.net/npm/component-register/+esm";

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner)
    return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (
    next &&
    !next._$owner &&
    !(next.assignedSlot && next.assignedSlot._$owner)
  )
    next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = "";
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(Component) {
  return (props, options) => {
    noShadowDOM();
    const comp = Component(props, options);
    return comp;
  };
}

const customShadowlessElement = (
  tagName,
  props = {},
  Component = (props, options) => {},
  ...rest
) =>
  compose(
    register(tagName, props),
    withNoShadowDOM,
    withSolid,
    ...rest,
  )(Component);

const QuantityInputContext = createContext((props) => {
  const [elementProps, stateProps] = splitProps(props, ["root", "input"]);
  const constrain = (value, min = -Infinity, max = Infinity) => {
    return Math.max(Math.min(value, max), min);
  };

  const validateQty = (value) => {
    if (typeof value === "string") {
      const newValue = parseInt(value);
      if (Number.isNaN(newValue)) return 1;
      return newValue;
    } else if (typeof value === "number") return value;
    else if (typeof value === "function") return value;
    return 1;
  };

  const updateQuantity = (value) => {
    if (typeof value === "function") {
      const currentValue = stateProps.value;
      return elementProps.root.setAttribute("value", value(currentValue));
    }

    return elementProps.root.setAttribute(
      "value",
      constrain(validateQty(value), 1),
    );
  };

  const increment = () => {
    updateQuantity((prev) => constrain(prev + 1, 1));
  };
  const decrement = () => {
    updateQuantity((prev) => constrain(prev - 1, 1));
  };

  return [
    { quantity: () => props.value, input: elementProps.input },
    { setQuantity: updateQuantity, increment, decrement },
  ];
});

customShadowlessElement("quantity-input", { value: 0 }, (props, options) => {
  const input = options.element.querySelector("input[name='quantity']");
  if (!input)
    return console.warn(
      "quantity-input: Must wrap an input element with a name of 'quantity'",
    );
  const [{ quantity }, { setQuantity }] = provide(
    QuantityInputContext,
    mergeProps(props, { root: options.element, input }),
    options.element,
  );

  createEffect(() => {
    const q = quantity();
    if (!q) return;
    const input = options.element.querySelector("input[name='quantity']");
    input.value = q;
  });
  const handleChange = (event) => {
    if (quantity === event.target.value) return;
    setQuantity(event.target.value);
  };
  input.addEventListener("change", handleChange);
  onCleanup(() => {
    input.removeEventListener("change", handleChange);
  });
});

customShadowlessElement(
  "quantity-input-button",
  {
    on: "",
    action: "",
    preventDefault: true,
  },
  (props, options) => {
    const [{ input }, { increment, decrement }] = consume(
      QuantityInputContext,
      options.element,
    );
    if (!props.on || !props.action)
      return console.warn(
        "quanity-input-button: Please provide an action and an on prop",
      );

    if (props.action !== "increment" && props.action !== "decrement")
      return console.warn(
        "quantity-input-button: Please use increment or decrement for the action",
      );
    const button = options.element.querySelector("button");

    createEffect(
      on(
        () => ({ on: props.on, action: props.action }),
        ({ on, action }) => {
          const handleClick = (e) => {
            if (props.preventDefault) e.preventDefault();

            if (action === "increment") {
              increment();
              input.dispatchEvent(new Event("change", { bubbles: true }));
            }
            if (action === "decrement") {
              decrement();
              input.dispatchEvent(new Event("change", { bubbles: true }));
            }
          };

          button.addEventListener(on, handleClick);

          onCleanup(() => {
            button.removeEventListener(on, handleClick);
          });
        },
      ),
    );
  },
);
